import * as React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { LegacyProductsFavoritesCountQuery } from 'codegen/generated/cf'
import { useLegacyProductsFavoritesCountQuery } from 'codegen/generated/cf'
import { useNonce } from 'hooks/use-nonce'

const BatchPrefetchedIdsContext = React.createContext<Set<number>>(new Set())
export const BatchPrefetchedIdsProvider = BatchPrefetchedIdsContext.Provider

import type { ContentCountFavoritesQuery, ContentIsProductFavoritedQuery } from '@web/gen/content'
import {
  useContentTotalCountFavoritesQuery,
  useContentFavoriteProductMutation,
  useContentIsProductFavoritedQuery,
  useContentUnfavoriteProductMutation,
  contentIsProductFavoritedQueryKey,
  contentTotalCountFavoritesQueryKey,
} from '@web/gen/content'

interface ContentMutateContext {
  prevIsFavorited: boolean
  prevCountFavorites: number
}
interface LegacyMutateContext {
  prevCountAndFavourite: LegacyProductsFavoritesCountQuery['cfmainProductsFavoritesCount']['favoritesCounts'][number]
}

const NONCE_TYPE = 'favorite_product_action_nonce_field'
const CF_URL = process.env.NEXT_PUBLIC_CF_URL

async function mutateFavoriteFromLegacy(productId: number, isFavorite: boolean, security: string) {
  const response = await fetch(`${CF_URL}/cfsecure/favorite/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      product_id: productId.toString(),
      product_page: 'false',
      favorite: isFavorite ? '1' : '-1',
      security,
    }),
  })
  return response.ok
}

export function useContentFavorite(productId: string) {
  const queryClient = useQueryClient()
  const isFavoritedKey = contentIsProductFavoritedQueryKey({ productId })
  const countFavoritesKey = contentTotalCountFavoritesQueryKey({ productId })

  async function onMutate(): Promise<ContentMutateContext> {
    // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
    await Promise.all([
      queryClient.cancelQueries({ queryKey: isFavoritedKey }),
      queryClient.cancelQueries({ queryKey: countFavoritesKey }),
    ])

    // Snapshot the previous value
    const { contentIsProductFavorited: prevIsFavorited } =
      queryClient.getQueryData<ContentIsProductFavoritedQuery>(isFavoritedKey)!
    const {
      contentCountFavorites: { total: prevCountFavorites },
    } = queryClient.getQueryData<ContentCountFavoritesQuery>(countFavoritesKey)!

    // Optimistically update to the new value
    queryClient.setQueryData<ContentIsProductFavoritedQuery>(isFavoritedKey, {
      contentIsProductFavorited: !prevIsFavorited,
    })
    queryClient.setQueryData<ContentCountFavoritesQuery>(countFavoritesKey, {
      contentCountFavorites: { total: prevCountFavorites + (prevIsFavorited ? -1 : 1), users: [] },
    })

    // Return a context object with the snapshotted value
    return { prevIsFavorited, prevCountFavorites }
  }

  function onError(context?: ContentMutateContext) {
    if (!context) {
      return
    }
    // If the mutation fails, use the context returned from onMutate to roll back
    queryClient.setQueryData<ContentIsProductFavoritedQuery>(isFavoritedKey, {
      contentIsProductFavorited: context.prevIsFavorited,
    })
    queryClient.setQueryData<ContentCountFavoritesQuery>(countFavoritesKey, {
      contentCountFavorites: { total: context.prevCountFavorites, users: [] },
    })
  }

  async function onSettled() {
    // Always refetch after error or success
    return await Promise.all([
      queryClient.invalidateQueries({ queryKey: isFavoritedKey }),
      queryClient.invalidateQueries({ queryKey: countFavoritesKey }),
    ])
  }

  const contentIsProductFavoritedQuery = useContentIsProductFavoritedQuery(
    { productId },
    { select: data => data.contentIsProductFavorited },
  )
  const contentCountFavoriteQuery = useContentTotalCountFavoritesQuery(
    { productId },
    { select: data => data.contentCountFavorites?.total },
  )

  const favoriteProductMutation = useContentFavoriteProductMutation({
    onMutate,
    onError: (_, __, context) => onError(context),
    onSettled,
  })
  const unfavoriteProductMutation = useContentUnfavoriteProductMutation({
    onMutate,
    onError: (_, __, context) => onError(context),
    onSettled,
  })

  const isLoadingQuery =
    contentIsProductFavoritedQuery.isPending || contentCountFavoriteQuery.isPending
  const isLoadingMutation = favoriteProductMutation.isPending || unfavoriteProductMutation.isPending
  const favoriteCount = contentCountFavoriteQuery.data
  const hasFavorited = contentIsProductFavoritedQuery.data ?? true

  const toggleFavoriteProduct = () => {
    if (hasFavorited) {
      unfavoriteProductMutation.mutate({ productId })
    } else {
      favoriteProductMutation.mutate({ productId })
    }
  }

  return {
    toggleFavoriteProduct,
    isLoadingQuery,
    isLoadingMutation,
    favoriteCount,
    hasFavorited,
  }
}

export function useLegacyFavorite(productId: number) {
  const queryClient = useQueryClient()
  const batchPrefetchedIds = React.useContext(BatchPrefetchedIdsContext)
  const isBatchPrefetched = batchPrefetchedIds.has(productId)
  const countAndFavoriteKey = useLegacyProductsFavoritesCountQuery.getKey({
    productIds: [productId],
  })

  async function onMutate(): Promise<LegacyMutateContext> {
    // Cancel any outgoing re-fetches (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey: countAndFavoriteKey })

    // Snapshot the previous value
    const { cfmainProductsFavoritesCount } =
      queryClient.getQueryData<LegacyProductsFavoritesCountQuery>(countAndFavoriteKey)!

    const prevCountAndFavourite = cfmainProductsFavoritesCount.favoritesCounts[0]

    // Optimistically update to the new value
    queryClient.setQueryData<LegacyProductsFavoritesCountQuery>(countAndFavoriteKey, {
      cfmainProductsFavoritesCount: {
        favoritesCounts: [
          {
            ...prevCountAndFavourite,
            count: prevCountAndFavourite.count + (prevCountAndFavourite.isFavorite ? -1 : 1),
            isFavorite: !prevCountAndFavourite.isFavorite,
          },
        ],
      },
    })

    // Return a context object with the snapshotted value
    return { prevCountAndFavourite }
  }

  function onError(context?: LegacyMutateContext) {
    if (!context) {
      return
    }
    // If the mutation fails, use the context returned from onMutate to roll back
    queryClient.setQueryData<LegacyProductsFavoritesCountQuery>(countAndFavoriteKey, {
      cfmainProductsFavoritesCount: { favoritesCounts: [context.prevCountAndFavourite] },
    })
  }

  const countAndFavouriteQuery = useLegacyProductsFavoritesCountQuery(
    { productIds: [productId] },
    {
      select: data => data.cfmainProductsFavoritesCount.favoritesCounts[0],
      staleTime: 30_000,
      enabled: !isBatchPrefetched,
    },
  )

  const { getNonce } = useNonce(() => onError())
  const favoriteMutation = useMutation({
    mutationFn: async (isFavorite: boolean) => {
      const nonceMutationLegacy = await getNonce(NONCE_TYPE)
      return await mutateFavoriteFromLegacy(productId, isFavorite, nonceMutationLegacy)
    },
    onMutate,
    onError: (_error: unknown, _variables: boolean, context: LegacyMutateContext | undefined) =>
      onError(context),
  })

  const isLoadingQuery = countAndFavouriteQuery.isPending
  const isLoadingMutation = favoriteMutation.isPending
  const favoriteCount = countAndFavouriteQuery.data?.count
  const hasFavorited = countAndFavouriteQuery.data?.isFavorite ?? true

  const toggleFavoriteProduct = () => {
    favoriteMutation.mutate(!hasFavorited)
  }

  return {
    toggleFavoriteProduct,
    isLoadingQuery,
    isLoadingMutation,
    favoriteCount,
    hasFavorited,
  }
}

export function usePrefetchLegacyFavoritesCounts(productIds: number[]) {
  const queryClient = useQueryClient()

  const uncachedProductIds = productIds.filter(id => {
    const key = useLegacyProductsFavoritesCountQuery.getKey({ productIds: [id] })
    return !queryClient.getQueryData(key)
  })

  const { data } = useLegacyProductsFavoritesCountQuery(
    { productIds: uncachedProductIds },
    { enabled: uncachedProductIds.length > 0, staleTime: 30_000 },
  )

  React.useEffect(() => {
    if (!data) {
      return
    }
    for (const item of data.cfmainProductsFavoritesCount.favoritesCounts) {
      const key = useLegacyProductsFavoritesCountQuery.getKey({
        productIds: [item.productId],
      })
      queryClient.setQueryData<LegacyProductsFavoritesCountQuery>(key, {
        cfmainProductsFavoritesCount: { favoritesCounts: [item] },
      })
    }
  }, [data, queryClient])
}
